import React from 'react';
import { ClipCostSummary } from '../../api/clip';
import { Badge, Button } from '../ui';
import { CostDisplayMode } from '../../hooks/useCostDisplay';
import { clipCostCredits, formatClipCost, formatSharedRunCost } from './clipPromptItemHelpers';
import { clipStyleLabel } from './clipStyleDisplay';

interface ClipPromptItemHeaderProps {
  clipName: string;
  clipStyle: string;
  /** `false` marks a style the renderer can't produce (registry flag). */
  styleRenderable?: boolean;
  costSummary: ClipCostSummary | null;
  display: CostDisplayMode;
  isCarouselClip: boolean;
  onOpenAiChat: (event: React.MouseEvent) => void;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onOpenSettings: () => void;
  onOpenDeleteConfirm: () => void;
}

const ClipPromptItemHeader: React.FC<ClipPromptItemHeaderProps> = ({
  clipName,
  clipStyle,
  styleRenderable,
  costSummary,
  display,
  isCarouselClip,
  onOpenAiChat,
  isExpanded,
  onToggleExpand,
  onOpenSettings,
  onOpenDeleteConfirm,
}) => (
  <div className="flex flex-col gap-2 mb-3 lg:flex-row lg:items-center lg:justify-between">
    <div className="flex flex-wrap items-center gap-2 min-w-0">
      <h3 className="text-white font-medium truncate max-w-full lg:max-w-xs">
        {clipName || 'Untitled Clip'}
      </h3>
      <Badge variant="gray">{clipStyleLabel(clipStyle)}</Badge>
      {styleRenderable === false && (
        <Badge
          variant="yellow"
          className="opacity-80"
          // title lives on a wrapper span since Badge renders a plain span
        >
          <span title="This style isn't supported by the renderer.">not renderable</span>
        </Badge>
      )}
      {costSummary && (costSummary.total_usd > 0 || clipCostCredits(costSummary) > 0) && (
        <span
          className={`text-sm ${display === 'credits' ? 'credit-estimate' : 'text-slate-400'}`}
          title="Spent on this clip: Generate Clip, regenerated takes, lip-sync, review"
        >
          {display === 'credits'
            ? `⚡ ${clipCostCredits(costSummary).toLocaleString()}`
            : formatClipCost(costSummary)}
        </span>
      )}
      {costSummary && formatSharedRunCost(costSummary, display) && (
        <span
          className="text-sm text-slate-400"
          title="Spent by the pipeline run this clip was assembled from — shared by every draft of that run"
        >
          {formatSharedRunCost(costSummary, display)}
        </span>
      )}
    </div>

    <div className="flex flex-wrap items-center gap-1 justify-start lg:justify-end">
      {isCarouselClip && (
        <button
          type="button"
          title="Open AI Editor chat for this carousel clip"
          aria-label={`Open AI Editor chat for ${clipName || 'carousel clip'}`}
          className="relative inline-flex min-h-[30px] items-center gap-2 border border-[#E4FF00] bg-[#E4FF00] px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.18em] text-black shadow-[2px_2px_0_0_#3f3f46] transition-all hover:-translate-x-px hover:-translate-y-px hover:bg-white hover:shadow-[3px_3px_0_0_#3f3f46] focus:outline-none focus:ring-1 focus:ring-[#E4FF00] whitespace-nowrap"
          onClick={onOpenAiChat}
        >
          <span className="relative flex h-2 w-2" aria-hidden="true">
            <span className="absolute inline-flex h-full w-full animate-ping bg-black opacity-70" />
            <span className="relative inline-flex h-2 w-2 bg-black" />
          </span>
          AI CHAT
        </button>
      )}
      <Button variant="ghost" size="sm" onClick={onToggleExpand}>
        {isExpanded ? 'Collapse' : 'Expand'}
      </Button>
      <Button variant="ghost" size="sm" onClick={onOpenSettings} title="Clip settings">
        ⚙
      </Button>
      <Button variant="ghost" size="sm" onClick={onOpenDeleteConfirm}>
        Delete
      </Button>
    </div>
  </div>
);

export default ClipPromptItemHeader;
